import {
  OS_VERSION_PATTERN,
  type IOsSupportPolicy,
  type OsFamily,
  type OsSupportStatus,
} from "@/features/servers/schemas/servers";

/**
 * Extrae YY.MM de strings tipo "20.04", "Ubuntu 20.04", "ubuntu-20.04".
 */
export function parseOsVersion(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const match = raw.trim().match(/(\d{2})\.(\d{2})/);
  if (!match) return null;
  const version = `${match[1]}.${match[2]}`;
  return OS_VERSION_PATTERN.test(version) ? version : null;
}

/** Compara YY.MM: negativo si a < b, 0 iguales, positivo si a > b. */
export function compareOsVersions(a: string, b: string): number {
  const pa = parseOsVersion(a);
  const pb = parseOsVersion(b);
  if (!pa || !pb) return 0;
  const [ay, am] = pa.split(".").map(Number);
  const [by, bm] = pb.split(".").map(Number);
  if (ay !== by) return ay - by;
  return am - bm;
}

export function resolveHostOsVersion(host: {
  osVersion?: string;
  os?: string;
}): string | null {
  return parseOsVersion(host.osVersion) ?? parseOsVersion(host.os);
}

/**
 * Evalúa el host contra la política (recomendado / mínimo soportado).
 */
export function evaluateOsSupport(
  hostVersion: string | null | undefined,
  policy: IOsSupportPolicy
): OsSupportStatus {
  const version = parseOsVersion(hostVersion ?? undefined);
  const recommended = parseOsVersion(policy.recommended);
  const minSupported = parseOsVersion(policy.minSupported);
  if (!version || !recommended || !minSupported) return "unknown";

  if (compareOsVersions(version, recommended) >= 0) return "recommended";
  if (compareOsVersions(version, minSupported) >= 0) return "supported";
  return "unsupported";
}

export function acceptsSoftwareUpdates(status: OsSupportStatus): boolean {
  return status === "recommended" || status === "supported";
}

/** Etiqueta de display: "Ubuntu 20.04 Desktop". */
export function formatOsLabel(
  family: OsFamily | undefined,
  version: string | undefined,
  fallbackOs?: string
): string {
  const v = parseOsVersion(version) ?? parseOsVersion(fallbackOs);
  if (family === "ubuntu" && v) return `Ubuntu ${v} Desktop`;
  if (family === "windows" && v) return `Windows ${v}`;
  if (family === "other" && v) return v;
  if (fallbackOs?.trim()) return fallbackOs.trim();
  if (v) return v;
  return "";
}
